import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { GraduationCap, Briefcase, HeartPulse, Building2, Landmark, Leaf } from "lucide-react";
import { PRIORITIES, fadeUp } from "../../data";

const ICONS = { GraduationCap, Briefcase, HeartPulse, Building2, Landmark, Leaf };

const TiltCard = ({ p, i }) => {
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 200, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 200, damping: 15 });
  const Icon = ICONS[p.icon];

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div variants={fadeUp} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }}
      ref={ref} onMouseMove={onMove} onMouseLeave={onLeave}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", transformPerspective: 900 }}
      data-testid={`priority-card-${i}`}
      className="group relative glass-dark rounded-2xl p-7 overflow-hidden">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: "radial-gradient(400px circle at 50% 0%, rgba(25,118,210,0.18), transparent 60%)" }} />
      <div style={{ transform: "translateZ(40px)" }} className="relative">
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl mb-5"
          style={{ background: "rgba(25,118,210,0.18)" }}>
          <Icon size={22} color="#5FA8F5" />
        </span>
        <h3 className="font-display font-semibold text-lg text-ivory">{p.title}</h3>
        <p className="font-body text-[13px] font-medium mt-1" style={{ color: "var(--gold)" }}>{p.head}</p>
        <p className="font-body text-ivory/85 text-sm leading-relaxed mt-3">{p.body}</p>
      </div>
    </motion.div>
  );
};

export const Vision = () => {
  return (
    <section id="vision" className="relative py-24 md:py-32 overflow-hidden" style={{ background: "var(--navy)" }}>
      <div className="absolute inset-0 opacity-60"
        style={{ background: "radial-gradient(900px circle at 20% 30%, rgba(13,43,102,0.9), transparent 50%)" }} />
      <div className="karnataka-bg absolute inset-y-0 left-0 w-1/2 opacity-[0.08]" style={{ backgroundImage: "url(/photos/karnataka.svg)" }} aria-hidden />
      <img src="/photos/congress.png" alt="" aria-hidden className="absolute top-10 right-10 w-40 md:w-56 opacity-[0.06] pointer-events-none select-none" />
      <div className="container-x relative z-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-3xl">
          <span className="eyebrow" style={{ color: "var(--gold)" }}>Vision for Karnataka</span>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[1.05] mt-5 text-ivory">
            A Karnataka where opportunity reaches <span style={{ color: "var(--gold)" }}>every generation.</span>
          </h2>
          <p className="font-body text-ivory/75 text-base md:text-lg leading-relaxed mt-7">
            I keep coming back to one simple idea: opportunity shouldn't depend on where you were born or who you know. I want a
            Karnataka where a good education is normal, where a young person can build a future close to home, and where
            everyday services just work — for everyone, not just a few.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-16">
          {PRIORITIES.map((p, i) => <TiltCard key={p.title} p={p} i={i} />)}
        </div>

        {/* quote */}
        <motion.blockquote variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="relative mt-24 max-w-4xl mx-auto text-center">
          <span className="font-display text-8xl leading-none block" style={{ color: "rgba(246,195,68,0.35)" }}>&ldquo;</span>
          <p className="font-display font-medium text-2xl md:text-3xl lg:text-4xl leading-snug text-ivory -mt-6">
            When every child can dream, every family feels safe, and every person has a fair shot at growing, we build a
            Karnataka that moves forward together.
          </p>
          <footer className="font-body mt-6 tracking-wide" style={{ color: "var(--gold)" }}>— Puttraj</footer>
        </motion.blockquote>
      </div>
    </section>
  );
};
